import { assetUrl } from "@/i18n/assets";
import { useState, ChangeEvent, FormEvent } from "react";
import { Seo } from "@/components/site/Seo";
import { PhoneInput } from "@/components/site/PhoneInput";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    seoTitle: "You're Invited — Stars, Stripes & Success",
    seoDesc: "RSVP for Axle Inc.'s Stars, Stripes & Success VIP appreciation event. Thursday September 17, 4:00–6:00 PM.",
    heroYears: "34 Years of Success",
    heroTitleMain: "Stars, Stripes",
    heroTitleAccent: "& Success",
    heroBody: "An evening of appreciation for the partners, customers, and community members who make our work possible.",
    heroAlt: "Gathering of Axle Inc. partners",
    inviteBody: "Please join us for an afternoon of great food, conversation, and celebration as we recognize the people and partnerships that have helped shape our success.",
    when: "When",
    whenDetails: "Thursday September 17",
    whenTime: "4:00 PM – 6:00 PM",
    where: "Where",
    whereDetails: "53664 County Road 9",
    whereCity: "Elkhart, IN 46514",
    thankYou: "Thank You",
    rsvpReceived: "Your RSVP has been graciously received.\nWe look forward to celebrating with you.",
    rsvpDeadline: "RSVP By September 10",
    name: "Name",
    business: "Business Name",
    phone: "Phone",
    email: "Email",
    attending: "Number Attending",
    errorMsg: "Something went wrong. Please try again.",
    sending: "Sending...",
    submit: "Confirm Attendance"
  },
  es: {
    seoTitle: "Está invitado — Estrellas, Franjas y Éxito",
    seoDesc: "Confirme su asistencia al evento de agradecimiento VIP Stars, Stripes & Success de Axle Inc. Jueves 17 de septiembre, 4:00–6:00 PM.",
    heroYears: "34 Años de Éxito",
    heroTitleMain: "Estrellas, Franjas",
    heroTitleAccent: "y Éxito",
    heroBody: "Una tarde de agradecimiento para los socios, clientes y miembros de la comunidad que hacen posible nuestro trabajo.",
    heroAlt: "Reunión de socios de Axle Inc.",
    inviteBody: "Acompáñenos a disfrutar de una tarde de excelente comida, conversación y celebración mientras reconocemos a las personas y asociaciones que han ayudado a forjar nuestro éxito.",
    when: "Cuándo",
    whenDetails: "Jueves 17 de septiembre",
    whenTime: "4:00 PM – 6:00 PM",
    where: "Dónde",
    whereDetails: "53664 County Road 9",
    whereCity: "Elkhart, IN 46514",
    thankYou: "Gracias",
    rsvpReceived: "Su confirmación de asistencia ha sido recibida con gratitud.\nEsperamos celebrar con usted.",
    rsvpDeadline: "Confirme antes del 10 de septiembre",
    name: "Nombre",
    business: "Nombre del Negocio",
    phone: "Teléfono",
    email: "Correo electrónico",
    attending: "Número de asistentes",
    errorMsg: "Algo salió mal. Por favor, intente de nuevo.",
    sending: "Enviando...",
    submit: "Confirmar Asistencia"
  }
};

const NAVY = "#151d3b";
const RED = "#c2202e";

function StarIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} style={style} aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function StarsDivider({ color = RED, className = "" }: { color?: string; className?: string }) {
  return (
    <div className={`flex items-center justify-center gap-3 ${className}`} aria-hidden="true">
      <StarIcon className="w-3 h-3 opacity-60" style={{ color }} />
      <StarIcon className="w-5 h-5" style={{ color }} />
      <StarIcon className="w-3 h-3 opacity-60" style={{ color }} />
    </div>
  );
}

type FormState = { name: string; businessName: string; phone: string; email: string; attending: string; website: string; };

const EMPTY: FormState = { name: "", businessName: "", phone: "", email: "", attending: "1", website: "" };

export default function Invite() {
  const t = useCopy(COPY);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const set = (key: keyof FormState) => (e: ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch("/api/invite-rsvp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          businessName: form.businessName,
          phone: form.phone,
          email: form.email,
          attending: form.attending,
          website: form.website,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        // Server errors are English-only; Spanish visitors get the localized message.
        setErrorMsg((t === COPY.es ? "" : data.error) || t.errorMsg);
        setStatus("error");
        return;
      }
      setStatus("sent");
    } catch {
      setErrorMsg(t.errorMsg);
      setStatus("error");
    }
  }

  const inputClass = "w-full bg-transparent border-0 border-b border-[#151d3b]/20 px-0 py-2.5 text-lg font-serif text-[#151d3b] placeholder:text-[#151d3b]/30 focus:outline-none focus:border-[#c9a24b] focus:ring-0 transition-colors rounded-none";
  const labelClass = "block text-[10px] font-bold uppercase tracking-[0.15em] text-[#151d3b]/50 mb-1";

  return (
    <main className="min-h-[100dvh] flex flex-col lg:flex-row bg-[#f8f4e9] text-[#151d3b] selection:bg-[#c9a24b] selection:text-white" style={{ "--font-serif": '"Playfair Display", Georgia, serif' } as React.CSSProperties}>
      <Seo title={t.seoTitle} description={t.seoDesc} path="/invite/" noindex />
      <section className="relative w-full lg:w-5/12 xl:w-1/2 h-[60vh] lg:h-screen lg:sticky lg:top-0 overflow-hidden flex flex-col justify-end">
        <div className="absolute inset-0 bg-[#151d3b]">
          <img src={assetUrl("/images/vip-appreciation-event.png")} alt={t.heroAlt} className="w-full h-full object-cover object-center opacity-60" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#151d3b] via-[#151d3b]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#151d3b]/80 via-[#151d3b]/40 to-transparent" />
        <div className="relative p-8 md:p-12 lg:p-16 xl:p-24 motion-safe:animate-fade-up">
          <p className="text-[#c9a24b] uppercase tracking-[0.3em] text-xs font-bold mb-6 flex items-center gap-3">
            <StarIcon className="w-3 h-3" />
            <span>{t.heroYears}</span>
            <StarIcon className="w-3 h-3" />
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-[#f8f4e9] tracking-tight leading-[1.05] mb-6">
            {t.heroTitleMain} <br className="hidden md:block" />
            <span className="italic text-[#c9a24b]">{t.heroTitleAccent}</span>
          </h1>
          <p className="text-lg md:text-xl font-light text-[#f8f4e9]/90 max-w-md leading-relaxed">{t.heroBody}</p>
        </div>
      </section>
      <section className="w-full lg:w-7/12 xl:w-1/2 flex-1 px-6 py-16 md:py-24 lg:px-16 xl:px-24 flex flex-col justify-center">
        <div className="max-w-xl mx-auto w-full">
          <div className="text-center mb-16 motion-safe:animate-fade-up" style={{ animationDelay: '150ms' }}>
            <img src={assetUrl("/brand/logo-red.webp")} alt="Axle Inc." className="h-12 md:h-14 w-auto mx-auto mb-8 object-contain" />
            <p className="font-serif text-xl md:text-2xl text-[#151d3b] leading-relaxed mb-10">{t.inviteBody}</p>
            <StarsDivider color={NAVY} className="mb-10 opacity-30" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <div>
                <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#c9a24b] mb-3">{t.when}</h3>
                <p className="font-serif text-lg text-[#151d3b] leading-snug">{t.whenDetails}<br /><span className="italic text-[#151d3b]/70">{t.whenTime}</span></p>
              </div>
              <div>
                <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#c9a24b] mb-3">{t.where}</h3>
                <p className="font-serif text-lg text-[#151d3b] leading-snug">{t.whereDetails}<br /><span className="italic text-[#151d3b]/70">{t.whereCity}</span></p>
              </div>
            </div>
          </div>
          <div className="bg-white p-8 md:p-12 shadow-2xl shadow-[#151d3b]/5 rounded-sm relative motion-safe:animate-fade-up" style={{ animationDelay: '300ms' }}>
            <div className="absolute inset-2 border border-[#c9a24b]/20 pointer-events-none" aria-hidden="true" />
            <div className="relative">
              {status === "sent" ? (
                <div className="py-16 text-center motion-safe:animate-fade-up">
                  <StarsDivider color={RED} className="mb-8" />
                  <h2 className="font-serif text-4xl text-[#151d3b] mb-4 italic">{t.thankYou}</h2>
                  <p className="text-[#151d3b]/70 text-lg leading-relaxed font-serif whitespace-pre-line">{t.rsvpReceived}</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-4 mb-10">
                    <div className="h-px bg-[#151d3b]/10 flex-1" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#c2202e]">{t.rsvpDeadline}</span>
                    <div className="h-px bg-[#151d3b]/10 flex-1" />
                  </div>
                  <form onSubmit={onSubmit} className="space-y-8">
                    <div aria-hidden="true" className="absolute -left-[9999px] -top-[9999px]">
                      <label htmlFor="rsvp-website">Website</label>
                      <input id="rsvp-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                      <div className="md:col-span-2">
                        <label htmlFor="rsvp-name" className={labelClass}>{t.name}</label>
                        <input id="rsvp-name" className={inputClass} value={form.name} onChange={set("name")} required maxLength={200} autoComplete="name" />
                      </div>
                      <div className="md:col-span-2">
                        <label htmlFor="rsvp-business" className={labelClass}>{t.business}</label>
                        <input id="rsvp-business" className={inputClass} value={form.businessName} onChange={set("businessName")} maxLength={200} autoComplete="organization" />
                      </div>
                      <div>
                        <label htmlFor="rsvp-phone" className={labelClass}>{t.phone}</label>
                        <PhoneInput id="rsvp-phone" className={inputClass} value={form.phone} onValueChange={(phone) => setForm((current) => ({ ...current, phone }))} required maxLength={40} autoComplete="tel" />
                      </div>
                      <div>
                        <label htmlFor="rsvp-email" className={labelClass}>{t.email}</label>
                        <input id="rsvp-email" type="email" className={inputClass} value={form.email} onChange={set("email")} required maxLength={200} autoComplete="email" />
                      </div>
                      <div className="md:col-span-2">
                        <label htmlFor="rsvp-attending" className={labelClass}>{t.attending}</label>
                        <input id="rsvp-attending" type="number" min={1} max={50} className={inputClass} value={form.attending} onChange={set("attending")} required />
                      </div>
                    </div>
                    {status === "error" && (
                      <div role="alert" aria-live="assertive" className="p-4 bg-[#c2202e]/5 border border-[#c2202e]/20 text-[#c2202e] text-sm font-medium text-center motion-safe:animate-fade-up">
                        {errorMsg}
                      </div>
                    )}
                    <button type="submit" disabled={status === "sending"} className="w-full mt-4 py-4 bg-[#151d3b] text-[#f8f4e9] text-[11px] font-bold uppercase tracking-[0.25em] transition-all hover:bg-[#c2202e] disabled:opacity-60 disabled:hover:bg-[#151d3b] flex justify-center items-center gap-3">
                      {status === "sending" ? t.sending : <><StarIcon className="w-3 h-3 text-[#c9a24b]" />{t.submit}<StarIcon className="w-3 h-3 text-[#c9a24b]" /></>}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
